import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Clock, Award, AlertTriangle, Flame, Timer, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const CertificateCountdown = ({ 
  enrollmentDate, 
  daysAllowed = 30, // Default 30 days to complete the course
  overallProgress = 0,
  compact = false,
  onStartCourse
}) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    totalSeconds: 0
  });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!enrollmentDate) return;

    const calculateTimeLeft = () => {
      const enrollDate = new Date(enrollmentDate);
      const deadline = new Date(enrollDate.getTime() + (daysAllowed * 24 * 60 * 60 * 1000));
      const now = new Date();
      const difference = deadline - now;

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, totalSeconds: 0 });
        return;
      }

      const totalSeconds = Math.floor(difference / 1000);
      const days = Math.floor(totalSeconds / (24 * 60 * 60));
      const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
      const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
      const seconds = totalSeconds % 60;

      setTimeLeft({ days, hours, minutes, seconds, totalSeconds });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [enrollmentDate, daysAllowed]);

  // Calculate urgency level based on time remaining
  const getUrgencyLevel = () => {
    const { days } = timeLeft;
    if (isExpired) return 'expired';
    if (days <= 3) return 'critical';
    if (days <= 7) return 'warning';
    return 'normal';
  };

  const urgency = getUrgencyLevel();

  // Format number with leading zero
  const formatNum = (num) => String(num).padStart(2, '0');

  // Get urgency-based styling
  const getUrgencyStyles = () => {
    switch (urgency) {
      case 'expired':
        return {
          bg: 'bg-gradient-to-br from-red-500 to-red-600',
          text: 'text-white',
          accent: 'text-red-100',
          icon: AlertTriangle,
          pulse: false
        };
      case 'critical':
        return {
          bg: 'bg-gradient-to-br from-orange-500 to-red-500',
          text: 'text-white',
          accent: 'text-orange-100',
          icon: Flame,
          pulse: true
        };
      case 'warning':
        return {
          bg: 'bg-gradient-to-br from-amber-400 to-orange-500',
          text: 'text-white',
          accent: 'text-amber-100',
          icon: Timer,
          pulse: false
        };
      default:
        return {
          bg: 'bg-gradient-to-br from-emerald-500 to-teal-600',
          text: 'text-white',
          accent: 'text-emerald-100',
          icon: Clock,
          pulse: false
        };
    }
  };

  const styles = getUrgencyStyles();
  const IconComponent = styles.icon;

  // Compact version for inline display
  if (compact) {
    if (!enrollmentDate || overallProgress >= 100) return null;
    
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
        urgency === 'critical' 
          ? 'bg-red-100 text-red-700 animate-pulse' 
          : urgency === 'warning'
            ? 'bg-amber-100 text-amber-700'
            : 'bg-emerald-100 text-emerald-700'
      }`}>
        <IconComponent className="w-4 h-4" />
        <span>
          {isExpired 
            ? 'Time expired' 
            : `${timeLeft.days}d ${formatNum(timeLeft.hours)}h left`
          }
        </span>
      </div>
    );
  }

  // If course is completed, show congratulations
  if (overallProgress >= 100) {
    return (
      <Card className="p-6 bg-gradient-to-br from-purple-600 to-indigo-700 text-white text-center">
        <Award className="w-12 h-12 mx-auto mb-3 text-yellow-300" />
        <h3 className="text-xl font-bold mb-1">Course Complete!</h3>
        <p className="text-purple-200 text-sm">
          Congratulations! You&apos;ve finished in time. Your certificate is ready!
        </p>
      </Card>
    );
  }

  // If no enrollment date, show prompt to start
  if (!enrollmentDate) {
    return (
      <Card className="p-6 bg-gradient-to-br from-slate-700 to-slate-800 text-white">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
            <Award className="w-7 h-7 text-emerald-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg">Start Your Journey</h3>
            <p className="text-slate-300 text-sm">
              Begin the course and get {daysAllowed} days to earn your certificate
            </p>
          </div>
          {onStartCourse && (
            <Button 
              onClick={onStartCourse}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              Start Now
            </Button>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card className={`p-6 ${styles.bg} ${styles.text} overflow-hidden relative`}>
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
      
      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full bg-white/20 flex items-center justify-center ${
              styles.pulse ? 'animate-pulse' : ''
            }`}>
              <IconComponent className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-lg">
                {isExpired 
                  ? 'Time Has Expired' 
                  : urgency === 'critical'
                    ? 'Hurry! Time Running Out'
                    : urgency === 'warning'
                      ? 'Complete Soon!'
                      : 'Certificate Deadline'
                }
              </h3>
              <p className={`text-sm ${styles.accent}`}>
                {isExpired 
                  ? 'Contact support to extend your deadline'
                  : `Complete the course to earn your certificate`
                }
              </p>
            </div>
          </div>
          
          {!isExpired && (
            <div className="hidden sm:block">
              <Award className={`w-10 h-10 ${urgency === 'critical' ? 'animate-bounce' : ''} opacity-50`} />
            </div>
          )}
        </div>

        {/* Countdown Timer */}
        {!isExpired && (
          <div className="grid grid-cols-4 gap-3 mb-4">
            {[
              { value: timeLeft.days, label: 'Days' },
              { value: timeLeft.hours, label: 'Hours' },
              { value: timeLeft.minutes, label: 'Minutes' },
              { value: timeLeft.seconds, label: 'Seconds' }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center"
              >
                <div className={`text-2xl sm:text-3xl font-bold font-mono ${
                  item.label === 'Seconds' ? 'tabular-nums' : ''
                }`}>
                  {formatNum(item.value)}
                </div>
                <div className={`text-xs ${styles.accent} uppercase tracking-wider`}>
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Progress indicator */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className={styles.accent}>Course Progress</span>
            <span className="font-semibold">{Math.round(overallProgress)}%</span>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>

        {/* Call to Action */}
        <div className="flex items-center justify-between gap-4">
          <p className={`text-sm ${styles.accent} flex-1`}>
            {isExpired 
              ? 'Your enrollment period has ended. You can still access the materials.'
              : urgency === 'critical'
                ? `Only ${timeLeft.days} day${timeLeft.days !== 1 ? 's' : ''} left! Complete your lessons now.`
                : urgency === 'warning'
                  ? `${timeLeft.days} days remaining. Stay on track to earn your certificate!`
                  : `You have ${timeLeft.days} days to complete the course and earn your certificate.`
            }
          </p>
          
          <Link to="/module/1">
            <Button 
              className={`${
                urgency === 'critical' 
                  ? 'bg-white text-red-600 hover:bg-red-50' 
                  : 'bg-white/20 hover:bg-white/30 text-white'
              } whitespace-nowrap`}
            >
              {isExpired ? 'Browse Lessons' : 'Continue'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </Card>
  );
};

export default CertificateCountdown;
